import { createFileRoute, Link, useLocation } from '@tanstack/react-router'
import {
  ArrowRight,
  Building2,
  CheckCircle2,
  DatabaseZap,
  Factory,
  Leaf,
  ShieldCheck,
  Wrench,
  Workflow,
} from 'lucide-react'
import { motion } from 'motion/react'
import { LandingPageLayout } from '@/components/landing/LandingPageLayout'
import { SectionScroller } from '@/components/landing/SectionScroller'
import { GridSpotlight } from '@/components/ui/grid-spotlight'
import { getIndustries, type IndustrySlug } from '@/data/industries'
import { designSystem } from '@/lib/design-system'
import { getLocaleFromPath, withLocalePath } from '@/lib/i18n-utils'
import { cn } from '@/lib/utils'

export const Route = createFileRoute('/{-$locale}/industries/')({
  head: () => ({
    meta: [
      { title: 'Industries — Ponte Solutions' },
      {
        name: 'description',
        content:
          'Industries Ponte Solutions serves: emergency lighting, building systems, industrial equipment, facility management, and agricultural automation.',
      },
      { property: 'og:title', content: 'Industries — Ponte Solutions' },
      {
        property: 'og:description',
        content:
          'Industries Ponte Solutions serves: emergency lighting, building systems, industrial equipment, facility management, and agricultural automation.',
      },
    ],
  }),
  component: IndustriesIndexPage,
})

const industryIcons = {
  'emergency-lighting': ShieldCheck,
  'building-systems': Building2,
  'industrial-equipment': Factory,
  'facility-management': Wrench,
  'agricultural-automation': Leaf,
} satisfies Record<IndustrySlug, typeof ShieldCheck>

const platformSteps = [
  {
    icon: DatabaseZap,
    title: 'Ingest the field data',
    text: 'Protocols, gateways, devices, and site conditions are handled before the data reaches analytics.',
  },
  {
    icon: CheckCircle2,
    title: 'Make it trustworthy',
    text: 'Signals become assets, events, states, sites, customers, and service context your teams can use.',
  },
  {
    icon: Workflow,
    title: 'Route the next action',
    text: 'Dashboards, alerts, cases, maintenance plans, and business-system updates close the loop.',
  },
]

const containerVariants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.09 } },
}

const itemVariants = {
  hidden: { opacity: 0, y: 18 },
  show: { opacity: 1, y: 0, transition: { duration: 0.45, ease: 'easeOut' } },
}

function onSectionMouseMove(e: React.MouseEvent<HTMLElement>) {
  const rect = e.currentTarget.getBoundingClientRect()
  e.currentTarget.style.setProperty('--spot-x', `${e.clientX - rect.left}px`)
  e.currentTarget.style.setProperty('--spot-y', `${e.clientY - rect.top}px`)
}

function IndustriesIndexPage() {
  return (
    <LandingPageLayout>
      <div className="ponte-service-page">
        <SectionScroller labels={['Industries', 'Pattern', 'Start']}>
          <IndustriesHeroSection />
          <IndustryPatternSection />
          <IndustriesCtaSection />
        </SectionScroller>
      </div>
    </LandingPageLayout>
  )
}

function IndustriesHeroSection() {
  const location = useLocation()
  const locale = getLocaleFromPath(location.pathname)
  const industries = getIndustries(locale)

  return (
    <section
      className="ponte-service-section ponte-service-section--hero relative isolate flex min-h-[calc(100svh-4rem)] items-center overflow-hidden px-4 py-10 text-white sm:px-6 lg:px-8"
      style={{ '--spot-x': '50%', '--spot-y': '50%' } as React.CSSProperties}
      onMouseMove={onSectionMouseMove}
    >
      <GridSpotlight />
      <div className="relative z-10 mx-auto grid w-full max-w-7xl items-center gap-10 lg:grid-cols-[0.95fr_1.05fr]">
        <motion.div
          className="max-w-3xl py-10"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, ease: 'easeOut' }}
        >
          <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/8 px-3 py-1.5 text-xs font-medium uppercase tracking-[0.14em] text-white/75">
            Industry fit
          </div>
          <h1
            className={cn(
              designSystem.typography.heading.h1,
              'max-w-4xl text-balance text-[clamp(2.7rem,7vw,5.4rem)] font-semibold leading-[0.94] tracking-normal text-white',
            )}
          >
            Connected product programs are{' '}
            <span className="text-accent">industry specific.</span>
          </h1>
          <p className="mt-7 max-w-2xl text-pretty text-lg leading-8 text-white/72 md:text-xl">
            Ponte works best where hardware is already in the field, customers expect service,
            and device data can become visibility, maintenance, automation, or recurring value.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to={withLocalePath('/contact', locale)}
              className="inline-flex min-h-11 items-center rounded-md bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground transition hover:bg-primary/90"
            >
              Discuss an Industry Fit
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
            <Link
              to={withLocalePath('/services/connect', locale)}
              className="inline-flex min-h-11 items-center rounded-md border border-white/20 px-5 py-3 text-sm font-semibold text-white transition hover:border-accent/70 hover:bg-white/8"
            >
              See the delivery model
            </Link>
          </div>
        </motion.div>

        <motion.div
          className="relative"
          variants={containerVariants}
          initial="hidden"
          animate="show"
        >
          <div className="relative rounded-2xl border border-white/12 bg-black/18 p-3 shadow-2xl shadow-black/30 backdrop-blur-md">
            <div className="grid gap-3 sm:grid-cols-2">
              {industries.map((industry, index) => {
                const Icon = industryIcons[industry.slug]
                const isFirst = index === 0
                return (
                  <motion.div key={industry.slug} variants={itemVariants} className={isFirst ? 'sm:col-span-2' : ''}>
                    <Link
                      to={withLocalePath(`/industries/${industry.slug}`, locale)}
                      className="group flex items-center justify-between gap-4 rounded-xl border border-white/10 bg-white/[0.055] p-5 transition hover:border-accent/35 hover:bg-white/[0.08]"
                    >
                      <div className="min-w-0">
                        <span className="font-mono text-xs text-accent/70">{industry.index}</span>
                        <h2 className={cn('mt-2 font-semibold text-white', isFirst ? 'text-xl' : 'text-lg')}>{industry.shortTitle}</h2>
                        <p className="mt-1 text-xs text-white/45">{industry.eyebrow}</p>
                        {isFirst && <p className="mt-2 max-w-lg text-sm leading-6 text-white/55">{industry.summary}</p>}
                      </div>
                      <div className="flex shrink-0 flex-col items-end gap-3">
                        <Icon className={cn('text-accent', isFirst ? 'h-6 w-6' : 'h-5 w-5')} />
                        <ArrowRight className="h-3.5 w-3.5 text-white/30 transition-transform group-hover:translate-x-0.5 group-hover:text-accent" />
                      </div>
                    </Link>
                  </motion.div>
                )
              })}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  )
}

function IndustryPatternSection() {
  return (
    <section className="ponte-service-section ponte-service-section--mesh relative isolate flex min-h-[calc(100svh-4rem)] items-center overflow-hidden px-4 py-16 text-white sm:px-6 lg:px-8">
      <div className="relative z-10 mx-auto grid w-full max-w-7xl gap-10 lg:grid-cols-[0.8fr_1.2fr] lg:items-center">
        <div>
          <span className="mb-4 block text-xs font-semibold uppercase tracking-[0.18em] text-accent">
            Shared pattern
          </span>
          <h2 className={cn(designSystem.typography.heading.h1, 'text-balance text-4xl font-semibold text-white md:text-5xl')}>
            The industries differ. The delivery loop stays clear.
          </h2>
          <p className="mt-6 max-w-xl text-lg leading-8 text-white/66">
            Each industry has its own constraints, protocols, and service model. The work still
            follows the same arc: capture the signal, make it reliable, then connect it to the
            business action.
          </p>
        </div>

        <div className="grid gap-4 md:grid-cols-3">
          {platformSteps.map((step, index) => (
            <motion.div
              key={step.title}
              className="rounded-xl border border-white/12 bg-white/[0.06] p-6 backdrop-blur"
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ duration: 0.45, delay: index * 0.08 }}
            >
              <div className="mb-8 flex items-center justify-between">
                <step.icon className="h-6 w-6 text-accent" />
                <span className="font-mono text-xs text-white/35">0{index + 1}</span>
              </div>
              <h3 className="text-xl font-semibold text-white">{step.title}</h3>
              <p className="mt-4 text-sm leading-7 text-white/60">{step.text}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  )
}

function IndustriesCtaSection() {
  const location = useLocation()
  const locale = getLocaleFromPath(location.pathname)

  return (
    <section
      className="ponte-service-section ponte-service-section--cta relative isolate flex min-h-[calc(100svh-4rem)] items-center overflow-hidden px-4 py-16 text-white sm:px-6 lg:px-8"
      style={{ '--spot-x': '50%', '--spot-y': '50%' } as React.CSSProperties}
      onMouseMove={onSectionMouseMove}
    >
      <GridSpotlight gridSize={56} baseOpacity={0.16} highlightOpacity={0.34} />
      <div className="relative z-10 mx-auto max-w-4xl text-center">
        <span className="mb-4 block text-xs font-semibold uppercase tracking-[0.18em] text-accent">
          Start with your installed base
        </span>
        <h2 className={cn(designSystem.typography.heading.h1, 'text-balance text-4xl font-semibold text-white md:text-6xl')}>
          Have a hardware category that should become more connected?
        </h2>
        <p className="mx-auto mt-6 max-w-2xl text-lg leading-8 text-white/66">
          We can map the device data, service model, business systems, and first useful
          connected-product use case before you commit to a full platform build.
        </p>
        <div className="mt-9 flex flex-wrap justify-center gap-3">
          <Link
            to={withLocalePath('/contact', locale)}
            className="inline-flex min-h-11 items-center rounded-md bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground transition hover:bg-primary/90"
          >
            Discuss a Project
            <ArrowRight className="ml-2 h-4 w-4" />
          </Link>
          <Link
            to={withLocalePath('/services/connect', locale)}
            className="inline-flex min-h-11 items-center rounded-md border border-white/20 px-5 py-3 text-sm font-semibold text-white transition hover:border-accent/70 hover:bg-white/8"
          >
            Explore Data Ingestion
          </Link>
        </div>
      </div>
    </section>
  )
}
